import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { ItemDoLote, LiberacaoDeGravacao, Lote, Produto, Unidade } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';

/**
 * Lotes (A8) e a geração de seriais (A9).
 *
 * <h3>Gerar serial é o ato mais sensível do sistema, e a tela mostra isso</h3>
 * Cada serial gerado é uma etiqueta que pode sair pela porta. A confirmação é explícita,
 * pede a quantidade digitada de novo, e o botão só aparece para {@code COMPANY_PROVISIONER}
 * — administrador <b>não basta</b>, e é assim no servidor também.
 *
 * <p>Não há como desfazer: serial gerado não é apagado, porque uma etiqueta impressa não
 * volta. O que existe é marcar o item, e isso é outra tela.
 */
export function LotesPage() {
  const { temPapel } = useAuth();
  const clienteDeConsulta = useQueryClient();

  const [productId, setProductId] = useState('');
  const [codigo, setCodigo] = useState('');
  const [previsto, setPrevisto] = useState(100);
  const [aberto, setAberto] = useState<string | null>(null);

  const lotes = useQuery({ queryKey: ['lotes'], queryFn: () => api.get<Lote[]>('/console/batches') });
  const produtos = useQuery({
    queryKey: ['produtos'],
    queryFn: () => api.get<Produto[]>('/console/products'),
  });

  const criar = useMutation({
    mutationFn: () =>
      api.post<Lote>('/console/batches', {
        productId: productId || produtos.data?.[0]?.id,
        code: codigo,
        plannedQty: previsto,
      }),
    onSuccess: () => {
      setCodigo('');
      void clienteDeConsulta.invalidateQueries({ queryKey: ['lotes'] });
    },
  });

  const porProduto = new Map((produtos.data ?? []).map((p) => [p.id, p.name]));

  return (
    <>
      <PageHeader
        titulo="Lotes"
        subtitulo="Um lote agrupa os itens de uma produção. É dele que os seriais nascem."
      />
      <div className="wrap">
        <div className="duas">
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="card tbl">
              {lotes.isPending && <Carregando o="os lotes" />}
              {lotes.isError && <Erro erro={lotes.error} />}
              {lotes.data && lotes.data.length === 0 && (
                <Vazio titulo="Nenhum lote" detalhe="Crie o primeiro ao lado." />
              )}
              {lotes.data && lotes.data.length > 0 && (
                <table className="t">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Produto</th>
                      <th>Previsto</th>
                      <th>Situação</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {lotes.data.map((l) => (
                      <tr key={l.id}>
                        <td className="mono-s" style={{ fontWeight: 600 }}>
                          {l.code}
                        </td>
                        <td>{porProduto.get(l.productId) ?? '—'}</td>
                        <td className="num">{l.plannedQty}</td>
                        <td>
                          <span className="chip chip-neutral">{l.status}</span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn-ghost"
                            onClick={() => setAberto(aberto === l.id ? null : l.id)}
                          >
                            {aberto === l.id ? 'Fechar' : 'Abrir'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {aberto && <DetalheDoLote batchId={aberto} />}
          </div>

          {temPapel('COMPANY_ADMIN', 'COMPANY_CATALOG') && (
            <div className="card cd">
              <div className="cd2-t">Novo lote</div>
              <form
                className="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  criar.mutate();
                }}
              >
                <div className="campo">
                  <label htmlFor="p">Produto</label>
                  <select
                    id="p"
                    value={productId || (produtos.data?.[0]?.id ?? '')}
                    onChange={(e) => setProductId(e.target.value)}
                  >
                    {(produtos.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="campo">
                  <label htmlFor="c">Código do lote</label>
                  <input id="c" value={codigo} onChange={(e) => setCodigo(e.target.value)} required />
                </div>
                <div className="campo">
                  <label htmlFor="q">Quantidade prevista</label>
                  <input
                    id="q"
                    type="number"
                    min={1}
                    value={previsto}
                    onChange={(e) => setPrevisto(Number(e.target.value))}
                  />
                  <span className="dica">
                    É uma expectativa, não um limite — a geração de seriais é um ato à parte.
                  </span>
                </div>
                {criar.isError && <div className="aviso ruim">{String(criar.error)}</div>}
                <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                  Criar lote
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function DetalheDoLote({ batchId }: { batchId: string }) {
  const { temPapel } = useAuth();
  const clienteDeConsulta = useQueryClient();
  const [quantidade, setQuantidade] = useState(10);
  const [confirmacao, setConfirmacao] = useState('');

  const itens = useQuery({
    queryKey: ['lote', batchId, 'itens'],
    queryFn: () => api.get<ItemDoLote[]>(`/console/batches/${batchId}/items`),
  });

  const gerar = useMutation({
    mutationFn: () => api.post<ItemDoLote[]>(`/console/batches/${batchId}/serials`, { qty: quantidade }),
    onSuccess: () => {
      setConfirmacao('');
      void clienteDeConsulta.invalidateQueries({ queryKey: ['lote', batchId, 'itens'] });
    },
  });

  const confere = confirmacao.trim() === String(quantidade);

  return (
    <div className="card cd">
      <div className="cd2-t">Itens do lote</div>

      {itens.isPending && <Carregando />}
      {itens.isError && <Erro erro={itens.error} />}
      {itens.data && (
        <div className="t-small" style={{ marginBottom: 12 }}>
          {itens.data.length} serial(is) gerado(s).
        </div>
      )}

      {/* A exportação é o passo seguinte do mesmo ato: fabricar identidade e mandá-la ao
          mundo físico. Por isso o mesmo papel, e por isso ela fica aqui e não numa tela à
          parte — quem acabou de gerar mil seriais precisa do arquivo na mesma visita. */}
      {temPapel('COMPANY_PROVISIONER') && itens.data && itens.data.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="t-small" style={{ marginBottom: 6 }}>
            Levar para a impressora ou para quem grava a etiqueta:
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <BotaoDeExportacao batchId={batchId} formato="csv" rotulo="Baixar CSV" />
            <BotaoDeExportacao batchId={batchId} formato="zpl" rotulo="Baixar ZPL" />
          </div>
        </div>
      )}

      {temPapel('COMPANY_PROVISIONER') && itens.data && itens.data.length > 0 && (
        <GravacaoPeloAparelho batchId={batchId} />
      )}

      {temPapel('COMPANY_PROVISIONER') ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (confere) {
              gerar.mutate();
            }
          }}
        >
          <div className="aviso">
            <strong>Serial gerado não volta.</strong> Cada um é uma etiqueta que pode sair pela
            porta, e a trilha registra quem gerou, quantos e quando.
          </div>
          <div className="linha">
            <div className="campo">
              <label htmlFor="qtd">Quantos gerar</label>
              <input
                id="qtd"
                type="number"
                min={1}
                max={10000}
                value={quantidade}
                onChange={(e) => setQuantidade(Number(e.target.value))}
              />
            </div>
            <div className="campo">
              <label htmlFor="conf">Digite a quantidade de novo</label>
              <input
                id="conf"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                placeholder={String(quantidade)}
              />
            </div>
          </div>
          {gerar.isError && <div className="aviso ruim">{String(gerar.error)}</div>}
          <button className="btn btn-danger" type="submit" disabled={!confere || gerar.isPending}>
            Gerar {quantidade} serial(is)
          </button>
        </form>
      ) : (
        <div className="t-small">
          Gerar seriais exige o papel <strong>Provisionamento</strong>. Administrador não basta —
          é o ato mais sensível do sistema, e ele pede uma mão própria.
        </div>
      )}

      {itens.data && itens.data.length > 0 && (
        <table className="t" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Código</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {itens.data.slice(0, 50).map((i) => (
              <tr key={i.id}>
                <td className="mono-s">{i.serial}</td>
                <td>
                  <span className="chip chip-neutral">{i.position}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Liberar o lote para uma unidade gravar as etiquetas pelo aplicativo (ADR-113).
 *
 * <h3>Por que liberar, e não só passar o código do lote</h3>
 * Código de lote é adivinhável. A liberação diz qual unidade pode gravar qual lote, e ela se
 * revoga no meio do trabalho sem apagar o que já foi gravado — as etiquetas conferidas continuam
 * verificando.
 *
 * <p>O servidor confere, ao liberar, o material de chave NFC e o endereço do passaporte. Os dois
 * fariam a gravação falhar na fábrica, com o gravador na mão e ninguém ali para resolver; o erro
 * aparece aqui, para quem pode.
 */
function GravacaoPeloAparelho({ batchId }: { batchId: string }) {
  const { traduzir } = useAuth();
  const clienteDeConsulta = useQueryClient();
  const [unidade, setUnidade] = useState('');

  const liberacoes = useQuery({
    queryKey: ['lote', batchId, 'liberacoes'],
    queryFn: () => api.get<LiberacaoDeGravacao[]>(`/console/batches/${batchId}/releases`),
    // O progresso anda enquanto a fábrica grava; a tela aberta acompanha sem recarregar.
    refetchInterval: 15_000,
  });
  const unidades = useQuery({
    queryKey: ['unidades'],
    queryFn: () => api.get<Unidade[]>('/console/units'),
  });

  const atualizar = () =>
    void clienteDeConsulta.invalidateQueries({ queryKey: ['lote', batchId, 'liberacoes'] });

  const liberar = useMutation({
    mutationFn: () =>
      api.post<LiberacaoDeGravacao>(`/console/batches/${batchId}/releases`, {
        unitId: unidade || unidades.data?.[0]?.id,
      }),
    onSuccess: atualizar,
  });
  const revogar = useMutation({
    mutationFn: (id: string) => api.post<LiberacaoDeGravacao>(`/console/releases/${id}/revoke`, {}),
    onSuccess: atualizar,
  });

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="t-small" style={{ marginBottom: 6 }}>
        Gravar as etiquetas NFC pelo aplicativo, numa unidade:
      </div>

      {liberacoes.data && liberacoes.data.length > 0 && (
        <table className="t" style={{ marginBottom: 10 }}>
          <thead>
            <tr>
              <th>Unidade</th>
              <th>Situação</th>
              <th>Gravadas</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {liberacoes.data.map((l) => (
              <tr key={l.id}>
                <td>{l.unidade}</td>
                <td>
                  <span className={l.status === 'ACTIVE' ? 'chip chip-teal' : 'chip chip-neutral'}>
                    {traduzir('release', l.status)}
                  </span>
                </td>
                <td className="num">
                  {l.gravadas} de {l.total}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {l.status === 'ACTIVE' && (
                    <button
                      className="btn btn-ghost"
                      disabled={revogar.isPending}
                      onClick={() => revogar.mutate(l.id)}
                    >
                      Revogar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          aria-label="Unidade que vai gravar"
          value={unidade || (unidades.data?.[0]?.id ?? '')}
          onChange={(e) => setUnidade(e.target.value)}
        >
          {(unidades.data ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.legalName}
            </option>
          ))}
        </select>
        <button
          className="btn"
          disabled={liberar.isPending || !unidades.data?.length}
          onClick={() => liberar.mutate()}
        >
          Liberar para gravação
        </button>
      </div>
      <div className="dica" style={{ marginTop: 6 }}>
        Na unidade, quem tem o papel <strong>Gravação de etiquetas</strong> vê o lote no aplicativo.
      </div>
      {liberar.isError && (
        <div className="aviso ruim" style={{ marginTop: 6 }}>
          {String(liberar.error)}
        </div>
      )}
      {revogar.isError && (
        <div className="aviso ruim" style={{ marginTop: 6 }}>
          {String(revogar.error)}
        </div>
      )}
    </div>
  );
}

/**
 * O botão de baixar, com o estado que ele precisa ter.
 *
 * Um lote de dez mil etiquetas não sai instantâneo, e um botão que não muda de aparência é um
 * botão que a pessoa aperta quatro vezes. O erro aparece ao lado, com o texto do servidor — e
 * o mais provável deles é útil: o endereço público do passaporte não está configurado neste
 * servidor, e por isso nenhuma etiqueta saiu apontando para lugar nenhum.
 */
function BotaoDeExportacao({
  batchId,
  formato,
  rotulo,
}: {
  batchId: string;
  formato: 'csv' | 'zpl';
  rotulo: string;
}) {
  const baixar = useMutation({
    mutationFn: () => api.baixar(`/console/batches/${batchId}/export?formato=${formato}`),
  });

  return (
    <div>
      <button className="btn" disabled={baixar.isPending} onClick={() => baixar.mutate()}>
        {baixar.isPending ? 'Gerando…' : rotulo}
      </button>
      {baixar.isError && (
        <div className="aviso ruim" style={{ marginTop: 6 }}>
          {String(baixar.error)}
        </div>
      )}
    </div>
  );
}
